// ── Apple Health (nutrition) ─────────────────────────────────────────
// Affinage is not a food diary and shouldn't become one. It knows one thing
// well — what you planned to cook and what's in it — and a diary app knows the
// rest: the granola bar, the restaurant meal, the barcode. Rather than grow a
// food database, we hand our half to HealthKit and let whichever diary app the
// cook prefers read it from there.
//
// That's the same shape as the Grocery bridge: we never talk to the other app,
// we write to the store it already reads.
//
// This module is the part that needs no device, so it can be reasoned about
// and tested here: which meals are worth logging, what they're worth, and when
// they happened.

import { mealMacros } from "./mealMacros";
import type { Macros, MealSlot, PlanDay, PlannedMeal, Recipe } from "./types";

/** The clock time a slot is logged at, local to the planned day.
 *
 *  HealthKit samples are timestamps, not slots, and diary apps bucket them
 *  into meals by time of day. A plan only says "dinner", so we pick the hour
 *  that lands a dinner in a diary's dinner. Exactness doesn't matter; being on
 *  the correct side of the boundaries does. */
export const SLOT_TIMES: Record<MealSlot, { hour: number; minute: number }> = {
  breakfast: { hour: 8, minute: 0 },
  lunch: { hour: 12, minute: 30 },
  snack: { hour: 15, minute: 30 },
  dinner: { hour: 19, minute: 0 },
};

/** A meal resolved into what actually goes to Health: scaled macros, and the
 *  moment to file them under. */
export type HealthEntry = {
  /** PlannedMeal.id — the key a written sample is remembered against, so a
   *  re-push replaces rather than duplicates. */
  mealId: string;
  title: string;
  /** Local wall-clock time of the sample, as an ISO string without a zone
   *  suffix so the device reads it in its own timezone — the day you planned
   *  is the day it should land on. */
  at: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
};

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** "2026-08-07" + "dinner" → "2026-08-07T19:00:00". Local, deliberately
 *  zoneless: see HealthEntry.at. */
export function entryTime(date: string, slot: MealSlot): string {
  const { hour, minute } = SLOT_TIMES[slot];
  return `${date}T${pad(hour)}:${pad(minute)}:00`;
}

/** Whether a meal has anything worth sending. A recipe with no macros at all
 *  would otherwise become an empty entry in the diary — a line that says a
 *  meal happened and contributed nothing, which is worse than no line. */
export function isLoggable(macros: Macros | undefined): boolean {
  if (!macros) return false;
  return (
    (macros.calories ?? 0) > 0 ||
    (macros.protein ?? 0) > 0 ||
    (macros.carbs ?? 0) > 0 ||
    (macros.fat ?? 0) > 0 ||
    (macros.fiber ?? 0) > 0
  );
}

/**
 * Turn a planned day into the entries to write.
 *
 * Only meals ticked as eaten. A plan is a statement of intent and changes
 * right up until the moment it doesn't happen — sending it to a food diary
 * would fill the diary with meals the cook never had. Ticking "eaten" is the
 * cook saying it actually happened, and that's the only claim worth recording.
 *
 * Recipe macros are per serving throughout Affinage (On Deck's day totals do
 * the same multiplication), so a meal's contribution is its recipe's macros
 * times the portions eaten — except where the cook corrected an ingredient's
 * amount for this meal, which mealMacros folds in.
 *
 * Two reasons a ticked meal produces no entry: its recipe carries no macros at
 * all, or its macros don't reconcile with its measured ingredients. Both are
 * reported (see unloggableMeals / unreconciledMeals) rather than dropped, so
 * the diary never quietly holds less than the cook ticked.
 */
export function planDayToEntries(
  day: PlanDay,
  byId: Map<string, Recipe>
): HealthEntry[] {
  const entries: HealthEntry[] = [];
  for (const meal of day.meals) {
    if (!meal.eaten) continue;
    const { macros, problem } = mealMacros(meal, byId.get(meal.recipeId));
    // A contradiction is not a number to file in a permanent health record.
    if (problem) continue;
    if (!isLoggable(macros)) continue;
    entries.push({
      mealId: meal.id,
      title: meal.title,
      at: entryTime(day.date, meal.slot),
      calories: macros.calories,
      protein: macros.protein,
      carbs: macros.carbs,
      fat: macros.fat,
      fiber: macros.fiber,
    });
  }
  return entries;
}

/** Meals the cook ticked as eaten that still can't be logged, because their
 *  recipe carries no macros. Reported rather than dropped silently — otherwise
 *  the diary quietly holds less than the cook ticked. */
export function unloggableMeals(
  day: PlanDay,
  byId: Map<string, Recipe>
): PlannedMeal[] {
  return day.meals.filter((m) => {
    if (!m.eaten) return false;
    const { macros, problem } = mealMacros(m, byId.get(m.recipeId));
    return !problem && !isLoggable(macros);
  });
}

/** Meals refused because the recipe's macros contradict its measured
 *  ingredients — the remainder guard in mealMacros.ts fired.
 *
 *  Kept apart from unloggableMeals because the two want different words: one
 *  is "there's nothing to send", the other is "these numbers can't both be
 *  true, and here's which". The second is fixable, so it carries the message. */
export function unreconciledMeals(
  day: PlanDay,
  byId: Map<string, Recipe>
): Array<{ meal: PlannedMeal; problem: string }> {
  const out: Array<{ meal: PlannedMeal; problem: string }> = [];
  for (const meal of day.meals) {
    if (!meal.eaten) continue;
    const { problem } = mealMacros(meal, byId.get(meal.recipeId));
    if (problem) out.push({ meal, problem });
  }
  return out;
}

/** How many of a day's meals are ticked as eaten — what the Health button will
 *  send, shown next to it so the effect is knowable before tapping. */
export function eatenCount(day: PlanDay | undefined): number {
  return (day?.meals ?? []).filter((m) => m.eaten).length;
}

/** Totals for the confirmation line, so the cook can sanity-check what landed
 *  against what On Deck showed them. */
export function entriesTotal(entries: HealthEntry[]): Required<Macros> {
  return entries.reduce(
    (t, e) => ({
      calories: t.calories + e.calories,
      protein: t.protein + e.protein,
      carbs: t.carbs + e.carbs,
      fat: t.fat + e.fat,
      fiber: t.fiber + e.fiber,
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
  );
}

// Re-pushing a day clears it first and writes it fresh, rather than diffing:
// servings change, recipes get edited, and HealthKit samples are immutable, so
// "take back our day, write what's true now" is the only version that can't
// leave a stale sample behind.
//
// Notably there is no bookkeeping of sample ids here. An earlier version kept
// mealId → uuid in Firestore and deleted by id; it double-counted every day,
// because deleting an HKCorrelation leaves the quantity samples inside it
// alive, and because remembered ids drift (lost on reinstall, or never saved
// if that write failed). The native side now clears by date, scoped by
// HealthKit to Affinage's own samples — no second source of truth to go wrong.
